"use client";

import clsx from "clsx";
import { usePathname } from "next/navigation";
import React, { useMemo } from "react";
import { BiCheckCircle, BiCircle } from "react-icons/bi";

import {
  APPLICATION_STEP_STATUS,
  PathType,
  getMessageForStatus,
} from "app/(PROTECTED)/customer/[applicationId]/_utils";
import { dateTimeFormat } from "constants/datetime";
import { ApplicationStatusValue, AdditionalInfo } from "constants/status";
import { ContractTypeEnum } from "graphql/type.ts";
import { cn } from "utils/cn";
import { formatDate } from "utils/formatDatetime";

const MessageBar = ({
  status,
  date,
  contractConfirmationStep,
  applicationContract = ContractTypeEnum.OnlineContract,
  additionalInfo,
}: {
  status: ApplicationStatusValue;
  date: string | null;
  contractConfirmationStep: number | undefined;
  applicationContract?: ContractTypeEnum;
  additionalInfo?: AdditionalInfo;
}) => {
  const pathname = usePathname();
  const splitPathname = pathname.split("/")[3] as PathType;

  const renderMessage = useMemo(
    () =>
      getMessageForStatus({
        status,
        pathname: splitPathname,
        contractConfirmationStep,
        applicationContract,
        additionalInfo,
      }),
    [status, splitPathname, contractConfirmationStep, applicationContract, additionalInfo]
  );

  const formattedDate = useMemo(() => {
    const isCompleted = renderMessage.status === APPLICATION_STEP_STATUS.COMPLETED;
    const isRejected = renderMessage.status === APPLICATION_STEP_STATUS.REJECTED;

    if (!date || (!isCompleted && !isRejected)) {
      return null;
    }

    const _formattedDate = formatDate(date, dateTimeFormat.DEFAULT);
    return `${isCompleted ? "完了" : "終了"} ${_formattedDate}`;
  }, [date, renderMessage]);

  return (
    <div>
      <div className="flex h-12 w-full">
        <div
          className={cn(
            "flex w-[110px] shrink-0 items-center justify-center",
            "bg-attention",
            renderMessage.status == APPLICATION_STEP_STATUS.UNCOMPLETED
              ? "bg-attention"
              : renderMessage.status == APPLICATION_STEP_STATUS.COMPLETED
              ? "bg-link"
              : "w-[109px] bg-warning"
          )}
        >
          {renderMessage.status == APPLICATION_STEP_STATUS.UNCOMPLETED ? (
            <BiCircle color="white" />
          ) : renderMessage.status == APPLICATION_STEP_STATUS.COMPLETED ? (
            <BiCheckCircle color="white" />
          ) : (
            <></>
          )}
          <span className="ml-1 text-s font-bold text-white">{renderMessage.status}</span>
        </div>
        <div
          className={clsx(
            "flex w-full items-center justify-between border px-6 text-14px text-sm",
            renderMessage.status == APPLICATION_STEP_STATUS.UNCOMPLETED
              ? "border-attention bg-attention-light"
              : renderMessage.status == APPLICATION_STEP_STATUS.COMPLETED
              ? "border-link bg-link-light"
              : "border-attention bg-attention-light"
          )}
        >
          <p className="flex-1 text-s">{renderMessage.message}</p>
          {!!formattedDate && <p className="text-s text-text-gray">{formattedDate}</p>}
        </div>
      </div>
    </div>
  );
};

export default MessageBar;
